import { hasValue } from '@m/magic-typescript';
import { useContext } from 'react';
import { RuntimeContext } from '../context';
import { File as FileType } from '../common/core-api';

interface UseFileProps {
  file: FileType;
  width?: number;
  height?: number;
  crop?: string;
}

const RAW = 'raw';

const UploadExtensions = {
  image: ['gif', 'jpeg', 'jpg', 'png'],
  video: ['mp4', '3gp', 'mov', 'avi', 'wmv', 'flv', 'mpeg', 'mpg', 'mp3', 'wav', 'mkv', 'webm', 'ogv'],
  raw: ['doc', 'docx', 'odt', 'ppt', 'pptx', 'xls', 'xlsx', 'csv', 'zip', 'rar', 'txt', 'rtf', 'pdf']
};

export const useFile = () => {
  const {
    cloudinaryUrl,
    community: { id: communityId },
    mirp
  } = useContext(RuntimeContext);

  const getUrl = ({ file, width, height, crop = 'fill' }: UseFileProps) => {
    const { extension } = file;
    const useMirp = mirp?.enabled === true && mirp?.url !== '';
    const raw = type(extension) === RAW;

    return useMirp
      ? `${mirp?.url}/display-media/${params({ width, height, crop })}${mirp?.region}/${communityId}/${fileName({
          file,
          useMirp
        })}`
      : `${cloudinaryUrl}/${type(extension)}/upload/${params({ width, height, crop })}${fileName({
          file,
          useMirp,
          raw
        })}`;
  };

  return [getUrl, UploadExtensions] as const;
};

const fileName = ({
  file,
  raw = false,
  urlencode = true,
  useMirp
}: {
  file: FileType;
  raw?: boolean;
  urlencode?: boolean;
  useMirp: boolean;
}) => {
  const name = urlencode ? encodeURIComponent(file.hash) : file.hash;
  return useMirp ? `${name}.${file.extension}` : `${name}${raw ? `.${file.extension}` : ''}`;
};

const params = ({ width, height, crop }: Pick<UseFileProps, 'crop' | 'height' | 'width'>) =>
  width ? [`w_${width}`, height && `h_${height}`, `c_${crop}`].filter(hasValue).join(',') + '/' : '';

const type = (extension: string) =>
  Object.entries(UploadExtensions)
    .map(([key, value]) => value.includes(extension) && key)
    .find(hasValue) || RAW;
